import { useState, useRef, useCallback } from 'react';
import { toast } from 'sonner';
import { fetchWithAuth } from '../../utils/api';
import { getToken } from '../../utils/tokenManager';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const IMAGE_EXTS = ['png', 'jpg', 'jpeg', 'webp'];

// Purpose: shared file-select logic used by both the single-file (CSV/PDF)
// path and the multi-image (screenshots) path. Kept in its own module so
// the main hook stays under the LOC gate.
//
// Routing rule:
//   1+ images → POST /api/institution-import/upload-screenshots (multi)
//   otherwise → POST /api/institution-import/upload (single file)
export function useFileUploadRouting({ onMatched, onUnmatched, onError, setStage }) {
  const [files, setFiles] = useState([]);
  const fileInputRef = useRef(null);

  const reset = useCallback(() => { setFiles([]); }, []);

  const routeAndUpload = useCallback(async (chosen) => {
    if (!chosen || chosen.length === 0) return;
    const list = Array.from(chosen);
    setFiles(list); setStage('reading');
    const allImages = list.every((f) => IMAGE_EXTS.includes(
      (f.name || '').split('.').pop().toLowerCase()
    ));
    try {
      const fd = new FormData();
      let url;
      if (list.length > 1 || (list.length === 1 && allImages)) {
        url = `${BACKEND_URL}/api/institution-import/upload-screenshots`;
        list.forEach((f) => fd.append('files', f));
      } else {
        url = `${BACKEND_URL}/api/institution-import/upload`;
        fd.append('file', list[0]);
      }
      const res = await fetch(url, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}` },
        body: fd,
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(body?.detail || "We couldn't read that file. Please try another.");
        onError?.(); reset(); return;
      }
      if (body.matched) onMatched?.(body);
      else onUnmatched?.(body);
    } catch {
      toast.error('Something went wrong while reading your file. Please try again.');
      onError?.(); reset();
    }
  }, [onMatched, onUnmatched, onError, reset, setStage]);

  return { files, setFiles, fileInputRef, routeAndUpload, reset };
}

export async function submitInstitutionRequest({ institutionName, countryCode, sourceFormat, notes }) {
  const res = await fetchWithAuth(`${BACKEND_URL}/api/institution-import/not-listed`, {
    method: 'POST',
    body: JSON.stringify({
      institution_name: institutionName.trim(),
      country_code: countryCode || 'ZZ',
      export_format: sourceFormat || 'csv',
      notes: notes || '',
    }),
  });
  const body = await res.json().catch(() => ({}));
  return { ok: res.ok, body };
}
